export interface PhoneCallClaim {
  id?: number;
  externalClaimId?: string;
  dateOfServiceFrom?: string;
  deliveryMethod?: string;
  status?: string;
  readyForSync?: boolean;
  referralId?: number;
  clientLabel?: string;
  serviceType?: string;
  assignedSpecialistName?: string;
}

export interface PhoneCallClaimsExtraction {
  all: PhoneCallClaim[];
  byReferralId: Record<number, PhoneCallClaim[]>;
}

function clientLabel(client: Record<string, unknown>): string {
  const first = String(client.privateFirstName || client.firstName || '').trim();
  const last = String(client.privateLastName || client.lastName || '').trim();
  return [first, last].filter(Boolean).join(' ') || 'Cliente';
}

function readField(record: Record<string, unknown>, camel: string, snake: string): unknown {
  return record[camel] ?? record[snake];
}

function parseSpecialistId(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return undefined;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return undefined;
}

function readReferralAssignedSpecialistId(referral: Record<string, unknown>): number | undefined {
  return parseSpecialistId(
    readField(referral, 'assignedSpecialistId', 'assigned_specialist_id')
  );
}

function readReferralAssignedSpecialistName(referral: Record<string, unknown>): string | undefined {
  const name = String(
    readField(referral, 'assignedSpecialistName', 'assigned_specialist_name') || ''
  ).trim();
  return name || undefined;
}

function nestedSpecialistRecord(
  source: Record<string, unknown>
): Record<string, unknown> | undefined {
  return (
    (source.specialist as Record<string, unknown> | undefined) ||
    (source.assignedSpecialist as Record<string, unknown> | undefined)
  );
}

/** Canalización de un referral: prioriza `assigned_specialist_id` del referral. */
export function isReferralAssigned(referral: Record<string, unknown>): boolean {
  return readReferralAssignedSpecialistId(referral) != null;
}

export function resolveAssignedSpecialistName(
  client: Record<string, unknown>,
  referral?: Record<string, unknown>
): string | undefined {
  if (referral) {
    const fromReferral = readReferralAssignedSpecialistName(referral);
    if (fromReferral) return fromReferral;
  }

  const fromClient = String(
    readField(client, 'assignedSpecialistName', 'assigned_specialist_name') || ''
  ).trim();
  if (fromClient) return fromClient;

  const ref = referral || {};
  for (const nested of [nestedSpecialistRecord(ref), nestedSpecialistRecord(client)]) {
    if (!nested) continue;
    const nestedName = String(nested.fullName || nested.name || '').trim();
    if (nestedName) return nestedName;
  }

  return undefined;
}

export function resolveAssignedSpecialistId(
  client: Record<string, unknown>,
  referral?: Record<string, unknown>
): number | undefined {
  if (referral) {
    const fromReferral = readReferralAssignedSpecialistId(referral);
    if (fromReferral != null) return fromReferral;
  }

  const fromClient = parseSpecialistId(
    readField(client, 'assignedSpecialistId', 'assigned_specialist_id')
  );
  if (fromClient != null) return fromClient;

  const ref = referral || {};
  for (const nested of [nestedSpecialistRecord(ref), nestedSpecialistRecord(client)]) {
    if (!nested) continue;
    const fromNested = parseSpecialistId(nested.specialistId ?? nested.id);
    if (fromNested != null) return fromNested;
  }

  return undefined;
}

/** Indica si un servicio tiene especialista canalizado. */
export function isReferralChannelized(
  client: Record<string, unknown>,
  referral?: Record<string, unknown>
): boolean {
  if (referral) return isReferralAssigned(referral);
  return resolveAssignedSpecialistId(client) != null || Boolean(resolveAssignedSpecialistName(client));
}

export interface IntakeServiceEntry {
  client: Record<string, unknown>;
  referral: Record<string, unknown> | null;
}

export function extractPhoneCallRoot(call: Record<string, unknown>): Record<string, unknown> {
  return (call.phonecall as Record<string, unknown> | undefined) || call;
}

/** Cliente real dentro de un ítem de `patients[]` (soporta `{ client: { referrals } }`). */
export function resolveIntakeClientRecord(row: Record<string, unknown>): Record<string, unknown> {
  const nested = row.client;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }
  return row;
}

function collectReferralsFromPatientRow(pat: Record<string, unknown>): Record<string, unknown>[] {
  const client = resolveIntakeClientRecord(pat);
  const fromClient = client.referrals;
  if (Array.isArray(fromClient) && fromClient.length > 0) {
    return fromClient as Record<string, unknown>[];
  }
  const fromPat = pat.referrals;
  if (Array.isArray(fromPat) && fromPat.length > 0) {
    return fromPat as Record<string, unknown>[];
  }
  const single = pat.referral ?? client.referral;
  if (single && typeof single === 'object' && !Array.isArray(single)) {
    return [single as Record<string, unknown>];
  }
  return [];
}

function collectPatientRowsFromRoot(root: Record<string, unknown>, call: Record<string, unknown>): Record<string, unknown>[] {
  if (Array.isArray(root.patients) && root.patients.length > 0) {
    return root.patients as Record<string, unknown>[];
  }
  if (Array.isArray(call.patients) && call.patients.length > 0) {
    return call.patients as Record<string, unknown>[];
  }
  if (root.patient) return [root.patient as Record<string, unknown>];
  if (call.patient) return [call.patient as Record<string, unknown>];
  return [];
}

/** Recopila beneficiarios/servicios (referrals) desde las distintas formas del API. */
export function collectIntakeServiceEntries(call: Record<string, unknown>): IntakeServiceEntry[] {
  const root = extractPhoneCallRoot(call);
  const entries: IntakeServiceEntry[] = [];
  const patientsList = collectPatientRowsFromRoot(root, call);

  if (patientsList.length > 0) {
    for (const pat of patientsList) {
      const client = resolveIntakeClientRecord(pat);
      const refs = collectReferralsFromPatientRow(pat);
      if (refs.length > 0) {
        for (const r of refs) {
          entries.push({ client, referral: r });
        }
      } else {
        entries.push({ client, referral: null });
      }
    }
    return entries;
  }

  if (Array.isArray(root.referrals) && root.referrals.length > 0) {
    for (const r of root.referrals) {
      entries.push({ client: {}, referral: r as Record<string, unknown> });
    }
    return entries;
  }

  const legacyRef = root.referral;
  if (legacyRef) {
    entries.push({ client: {}, referral: legacyRef as Record<string, unknown> });
  }

  return entries;
}

export function isIntakeServiceChannelized(entry: IntakeServiceEntry): boolean {
  if (entry.referral) return isReferralAssigned(entry.referral);
  return isReferralChannelized(entry.client);
}

function mapRawClaim(
  claim: Record<string, unknown>,
  meta?: {
    referralId?: number;
    clientLabel?: string;
    serviceType?: string;
    assignedSpecialistName?: string;
  }
): PhoneCallClaim {
  return {
    id: typeof claim.id === 'number' ? claim.id : undefined,
    externalClaimId: claim.externalClaimId as string | undefined,
    dateOfServiceFrom: claim.dateOfServiceFrom as string | undefined,
    deliveryMethod: claim.deliveryMethod as string | undefined,
    status: claim.status as string | undefined,
    readyForSync: Boolean(claim.readyForSync),
    referralId: meta?.referralId,
    clientLabel: meta?.clientLabel,
    serviceType: meta?.serviceType,
    assignedSpecialistName: meta?.assignedSpecialistName
  };
}

function extractClaimsFromReferral(
  referral: Record<string, unknown>,
  meta: {
    referralId?: number;
    clientLabel?: string;
    serviceType?: string;
    assignedSpecialistName?: string;
  }
): PhoneCallClaim[] {
  const claims = referral.claims;
  if (!Array.isArray(claims)) return [];
  return claims.map((claim) =>
    mapRawClaim(claim as Record<string, unknown>, {
      referralId: meta.referralId,
      clientLabel: meta.clientLabel,
      serviceType: meta.serviceType || String(referral.product || ''),
      assignedSpecialistName: meta.assignedSpecialistName
    })
  );
}

function mergeReferralClaims(
  all: PhoneCallClaim[],
  byReferralId: Record<number, PhoneCallClaim[]>,
  client: Record<string, unknown> | undefined,
  referral: Record<string, unknown>,
  meta: { referralId?: number; clientLabel?: string; serviceType?: string }
) {
  const assignedSpecialistName = resolveAssignedSpecialistName(client || {}, referral);
  const mapped = extractClaimsFromReferral(referral, {
    ...meta,
    serviceType: meta.serviceType || String(referral.product || ''),
    assignedSpecialistName
  });
  all.push(...mapped);
  if (typeof meta.referralId === 'number') {
    byReferralId[meta.referralId] = mapped;
  }
}

export function extractClaimsFromPhoneCall(root: Record<string, unknown>): PhoneCallClaimsExtraction {
  const all: PhoneCallClaim[] = [];
  const byReferralId: Record<number, PhoneCallClaim[]> = {};

  const patientsArr = root.patients as unknown[] | undefined;
  if (Array.isArray(patientsArr) && patientsArr.length > 0) {
    for (const patient of patientsArr) {
      const pat = patient as Record<string, unknown>;
      const client = resolveIntakeClientRecord(pat);
      const label = clientLabel(client);
      const referrals = collectReferralsFromPatientRow(pat);
      for (const referral of referrals) {
        const ref = referral as Record<string, unknown>;
        const referralId = typeof ref.id === 'number' ? ref.id : undefined;
        mergeReferralClaims(all, byReferralId, client, ref, {
          referralId,
          clientLabel: label,
          serviceType: String(ref.product || '')
        });
      }
    }
  } else {
    const singlePatient = root.patient as Record<string, unknown> | undefined;
    if (singlePatient) {
      const label = clientLabel(singlePatient);
      const referrals = (singlePatient.referrals as unknown[]) || [];
      if (referrals.length > 0) {
        for (const referral of referrals) {
          const ref = referral as Record<string, unknown>;
          const referralId = typeof ref.id === 'number' ? ref.id : undefined;
          mergeReferralClaims(all, byReferralId, singlePatient, ref, {
            referralId,
            clientLabel: label,
            serviceType: String(ref.product || '')
          });
        }
      } else {
        const legacyReferral = root.referral as Record<string, unknown> | undefined;
        if (legacyReferral) {
          const referralId = typeof legacyReferral.id === 'number' ? legacyReferral.id : undefined;
          mergeReferralClaims(all, byReferralId, singlePatient, legacyReferral, {
            referralId,
            clientLabel: label,
            serviceType: String(legacyReferral.product || '')
          });
        }
      }
    } else {
      const legacyReferral = root.referral as Record<string, unknown> | undefined;
      if (legacyReferral) {
        const referralId = typeof legacyReferral.id === 'number' ? legacyReferral.id : undefined;
        mergeReferralClaims(all, byReferralId, undefined, legacyReferral, {
          referralId,
          serviceType: String(legacyReferral.product || '')
        });
      }
    }
  }

  return { all, byReferralId };
}
